import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import CategoryMenuBar from "../components/CategoryMenuBar";
import CartIcon from "../components/CartIcon";
import { useCart } from "../hooks/useCart";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";
import "./Products.css";

const PLATFORM_HERO_SLIDES = [
  {
    id: "platform-1",
    image: null,
    title: "Discover Beauty That Performs",
    subtitle: "Platform-curated products trusted by verified professionals",
  },
  {
    id: "platform-2",
    image: null,
    title: "From Routine To Results",
    subtitle: "Find high-performing essentials for hair, skin, and wellness",
  },
  {
    id: "platform-3",
    image: null,
    title: "New Season, New Favorites",
    subtitle: "Fresh picks, standout formulas, and must-have upgrades",
  },
  {
    id: "platform-4",
    image: null,
    title: "Clean Beauty, Real Results",
    subtitle: "Browse clean formulations selected for quality and performance",
  },
  {
    id: "platform-5",
    image: null,
    title: "Editor Picks This Week",
    subtitle: "Trending products chosen by platform editors and top stylists",
  },
];

const normalizeProductsHeroSlides = (settings) => {
  const heroSettings = settings?.productsPageHero;
  if (!heroSettings || heroSettings.enabled === false) {
    return [];
  }

  const configuredSlides = Array.isArray(heroSettings.slides)
    ? heroSettings.slides
    : [];

  const cleanedSlides = configuredSlides
    .filter((slide) => slide && slide.isActive !== false)
    .map((slide, idx) => ({
      id: slide.id || `platform-${idx + 1}`,
      image: String(slide.imageUrl || "").trim() || null,
      title: String(slide.title || "").trim(),
      subtitle: String(slide.subtitle || "").trim(),
      ctaLabel: String(slide.ctaLabel || "").trim(),
      ctaUrl: String(slide.ctaUrl || "").trim(),
      position:
        Number.isFinite(slide.position) && slide.position > 0
          ? slide.position
          : idx + 1,
    }))
    .filter((slide) => slide.title || slide.subtitle || slide.image)
    .sort((a, b) => a.position - b.position)
    .slice(0, 5);

  return cleanedSlides;
};

const normalizeRadiusOptions = (rawOptions) => {
  if (!Array.isArray(rawOptions)) {
    return [5, 10, 25, 50];
  }

  const normalized = rawOptions
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 200);

  const uniqueSorted = [...new Set(normalized)].sort((a, b) => a - b);
  return uniqueSorted.length > 0 ? uniqueSorted : [5, 10, 25, 50];
};

function Products() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const {
    inputValue: locationInput,
    suggestions: locationSuggestions,
    selectedCoords: userCoords,
    locationSource,
    geolocationStatus,
    showSuggestions,
    handleInputChange: handleLocationInputChange,
    selectSuggestion: handleLocationSuggestionSelect,
    clearLocation,
    setShowSuggestions,
  } = useGooglePlacesAutocomplete();

  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || "all",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("popularity");
  const [sortTouched, setSortTouched] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [flashMessage, setFlashMessage] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(PLATFORM_HERO_SLIDES);
  const [heroRotateMs, setHeroRotateMs] = useState(4500);
  const [radiusOptions, setRadiusOptions] = useState([5, 10, 25, 50]);
  const [radiusMiles, setRadiusMiles] = useState("10");
  const [searchSettingsLoaded, setSearchSettingsLoaded] = useState(false);

  useEffect(() => {
    const incomingNotice = String(location.state?.storefrontNotice || "").trim();
    if (!incomingNotice) return undefined;

    setFlashMessage(incomingNotice);
    const timeoutId = window.setTimeout(() => {
      setFlashMessage("");
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [location.state]);

  useEffect(() => {
    const fetchPublicSearchSettings = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/public`,
        );
        if (!response.ok) return;

        const data = await response.json();
        const homepageSettings = data?.settings?.homepageSettings || {};
        const configuredOptions = normalizeRadiusOptions(
          homepageSettings.publicSearchRadiusOptions,
        );
        const configuredDefault = Number(
          homepageSettings.publicSearchDefaultRadiusMiles,
        );
        const fallbackDefault = configuredOptions.includes(10)
          ? 10
          : configuredOptions[0];
        const nextDefault = configuredOptions.includes(configuredDefault)
          ? configuredDefault
          : fallbackDefault;

        setRadiusOptions(configuredOptions);
        setRadiusMiles(String(nextDefault));
      } catch (err) {
        setRadiusOptions([5, 10, 25, 50]);
      } finally {
        setSearchSettingsLoaded(true);
      }
    };

    fetchPublicSearchSettings();
  }, []);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories`,
        );
        if (response.ok) {
          const data = await response.json();
          setCategories(data || []);
        }
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const displayedSortValue = userCoords && !sortTouched ? "distance" : sortBy;
  const usingBrowserLocation = locationSource === "browser";
  const usingManualLocation = locationSource === "manual";

  const handleSortChange = (e) => {
    if (e.target.value === "distance") {
      setSortTouched(false);
      return;
    }

    setSortBy(e.target.value);
    setSortTouched(true);
  };

  const handleLocationSelect = (prediction) => {
    setSortTouched(false);
    handleLocationSuggestionSelect(prediction);
  };

  const handleClearLocation = () => {
    setSortTouched(false);
    clearLocation();
  };

  // Fetch all featured/public products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("limit", "100");

        if (userCoords) {
          params.append("lat", String(userCoords.lat));
          params.append("lng", String(userCoords.lng));
          params.append("radius", radiusMiles);

          if (locationSource === "manual") {
            const parts = userCoords.label?.split(",").map((value) => value.trim());
            const searchCity = parts?.[0];
            const stateFromLabel = parts?.[1]?.split(" ")[0];
            if (searchCity) params.append("searchCity", searchCity);
            if (stateFromLabel) params.append("state", stateFromLabel);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/featured?${params.toString()}`,
        );
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
        } else {
          setError("Failed to load products");
        }
      } catch (err) {
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geolocationStatus, searchSettingsLoaded, userCoords, radiusMiles, locationSource]);

  useEffect(() => {
    const fetchProductsHeroSettings = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/public`,
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const publicSettings = data?.settings || {};

        const slidesFromSettings = normalizeProductsHeroSlides(publicSettings);
        if (slidesFromSettings.length > 0) {
          setHeroSlides(slidesFromSettings);
        }

        const configuredRotateMs = publicSettings?.productsPageHero?.autoRotateMs;
        if (
          Number.isFinite(configuredRotateMs) &&
          configuredRotateMs >= 2000 &&
          configuredRotateMs <= 20000
        ) {
          setHeroRotateMs(configuredRotateMs);
        }
      } catch (error) {
        // Keep fallback hero slides if public settings fetch fails
      }
    };

    fetchProductsHeroSettings();
  }, []);

  useEffect(() => {
    if (activeSlide >= heroSlides.length) {
      setActiveSlide(0);
    }
  }, [activeSlide, heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, heroRotateMs);

    return () => window.clearInterval(timer);
  }, [heroSlides, heroRotateMs]);

  // Handle category click
  const handleCategoryClick = (categoryValue) => {
    setSelectedCategory(categoryValue);
    if (categoryValue === "all") {
      navigate("/products");
    } else {
      navigate(`/products?category=${categoryValue}`);
    }
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    // Category filter (handle both array and string formats)
    if (selectedCategory !== "all") {
      const productCategories = Array.isArray(product.category)
        ? product.category
        : product.category
          ? [product.category]
          : [];

      if (
        productCategories.length === 0 ||
        !productCategories.includes(selectedCategory)
      ) {
        return false;
      }
    }

    // Search filter
    if (
      searchQuery &&
      !product.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (userCoords && !sortTouched) {
      return 0;
    }

    switch (sortBy) {
      case "price-low":
        return (a.price || 0) - (b.price || 0);
      case "price-high":
        return (b.price || 0) - (a.price || 0);
      case "newest":
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      case "popularity":
      default:
        return (b.quantitySold || 0) - (a.quantitySold || 0);
    }
  });

  // Handle product click
  const handleProductClick = (product) => {
    const shopId =
      typeof product.shopId === "object" ? product.shopId._id : product.shopId;
    navigate(`/product/${product._id}?shopId=${shopId}`);
  };

  const hasSelectableVariants = (product) => {
    if (!product || typeof product !== "object") return false;
    if (product.hasVariants === true) return true;
    if (Number(product.variantCount) > 1) return true;
    if (Array.isArray(product.variants) && product.variants.length > 1) {
      return true;
    }
    return false;
  };

  const getSellerDisplayName = (product) => {
    const shop =
      product?.shopId && typeof product.shopId === "object"
        ? product.shopId
        : null;

    return (
      product?.displayBrandName ||
      product?.displayBusinessName ||
      product?.businessName ||
      shop?.displayBrandName ||
      shop?.displayBusinessName ||
      shop?.businessName ||
      shop?.businessId?.name ||
      ""
    );
  };

  return (
    <div className="products-page-shell">
      <CustomerHeader />

      {flashMessage && (
        <div
          style={{
            maxWidth: "1200px",
            margin: "14px auto 0",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #fde68a",
            background: "#fffbeb",
            color: "#92400e",
            fontSize: "14px",
            fontWeight: 600,
          }}
          role="status"
          aria-live="polite"
        >
          {flashMessage}
        </div>
      )}

      {/* Category Menu Bar - Mobile Only */}
      <CategoryMenuBar
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryClick={handleCategoryClick}
        mode="mobile-only"
        showCategoriesLabel={false}
        initialVisibleCount={20}
      />

      {/* Fixed Cart Icon */}
      <CartIcon />

      {heroSlides.length > 0 && (
      <section
        className="products-hero"
        aria-label="Featured products highlights"
      >
        <div
          className="products-hero-track"
          style={{ transform: `translateX(-${activeSlide * 100}%)` }}
        >
          {heroSlides.map((slide) => (
            <article key={slide.id} className="products-hero-slide">
              {slide.image ? (
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="products-hero-image"
                />
              ) : (
                <div className="products-hero-gradient" />
              )}
              <div className="products-hero-overlay">
                <h2>{slide.title}</h2>
                <p>{slide.subtitle}</p>
                {slide.ctaLabel && slide.ctaUrl && (
                  <a className="products-hero-cta" href={slide.ctaUrl}>
                    {slide.ctaLabel}
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
        <div className="products-hero-dots">
          {heroSlides.map((slide, idx) => (
            <button
              key={slide.id}
              type="button"
              className={`products-hero-dot${idx === activeSlide ? " products-hero-dot--active" : ""}`}
              onClick={() => setActiveSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </section>
      )}

      <div className="global-products-page">
        <div className="products-page-layout">
          {/* Category Sidebar - Desktop Only (Column 1) */}
          <CategoryMenuBar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryClick={handleCategoryClick}
            mode="sidebar-only"
            showCategoriesLabel={false}
            initialVisibleCount={20}
          />

          {/* Main Content (Column 2) */}
          <div className="products-main-content">
            {/* Search and Sort Controls */}
            <div className="products-controls products-controls-desktop">
              <div className="products-location-control">
                <div className="products-location-input-wrap products-location-input-wrap--inline">
                  <input
                    type="text"
                    placeholder="Search near a city or address..."
                    value={locationInput}
                    onChange={(e) => handleLocationInputChange(e.target.value)}
                    onFocus={() =>
                      locationSuggestions.length > 0 && setShowSuggestions(true)
                    }
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="products-location-input"
                  />
                  {locationInput && (
                    <button
                      type="button"
                      onClick={handleClearLocation}
                      className="products-location-clear"
                      aria-label="Clear location"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {showSuggestions && locationSuggestions.length > 0 && (
                  <div className="products-location-suggestions">
                    {locationSuggestions.map((pred) => (
                      <button
                        key={pred.place_id}
                        type="button"
                        onMouseDown={() => handleLocationSelect(pred)}
                        className="products-location-suggestion"
                      >
                        📍 {pred.description}
                      </button>
                    ))}
                  </div>
                )}

              </div>

              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="sort-dropdown">
                <label>Sort by:</label>
                <select
                  value={displayedSortValue}
                  onChange={handleSortChange}
                >
                  <option value="distance" disabled={!userCoords}>
                    Nearest
                  </option>
                  <option value="popularity">Popularity</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
              <div className="sort-dropdown products-radius-dropdown">
                <label>Radius:</label>
                <select
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(e.target.value)}
                >
                  {radiusOptions.map((radius) => (
                    <option key={radius} value={String(radius)}>
                      {radius} mi
                    </option>
                  ))}
                </select>
              </div>

              <div className="products-location-summary">
                {userCoords ? (
                  <p className="products-location-note products-location-note--active">
                    {usingBrowserLocation ? (
                      <>
                        Showing pickup products closest to <strong>your location</strong>,
                        sorted by distance ({radiusMiles} mi): nearest pickup products first.
                      </>
                    ) : sortTouched ? (
                      <>
                        Showing pickup products closest to <strong>{userCoords.label}</strong>,
                        sorted by {displayedSortValue === "distance" ? "nearest" : displayedSortValue}.
                      </>
                    ) : (
                      <>
                        Showing pickup products closest to <strong>{userCoords.label}</strong>,
                        sorted by distance ({radiusMiles} mi):
                        {usingManualLocation
                          ? " searched city first, then nearby pickup products."
                          : " nearest pickup products first."}
                      </>
                    )}
                  </p>
                ) : (
                  <p className="products-location-note">
                    Location access is off, so results use the default discovery feed.
                  </p>
                )}
              </div>
            </div>

            <div className="products-controls-mobile">
              <div className="products-controls-mobile-top-row">
                <div className="products-location-control products-location-control--mobile">
                  <div className="products-location-input-wrap products-location-input-wrap--inline">
                    <input
                      type="text"
                      placeholder="Search near a city or address..."
                      value={locationInput}
                      onChange={(e) => handleLocationInputChange(e.target.value)}
                      onFocus={() =>
                        locationSuggestions.length > 0 && setShowSuggestions(true)
                      }
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      className="products-location-input"
                    />
                    {locationInput && (
                      <button
                        type="button"
                        onClick={handleClearLocation}
                        className="products-location-clear"
                        aria-label="Clear location"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {showSuggestions && locationSuggestions.length > 0 && (
                    <div className="products-location-suggestions">
                      {locationSuggestions.map((pred) => (
                        <button
                          key={pred.place_id}
                          type="button"
                          onMouseDown={() => handleLocationSelect(pred)}
                          className="products-location-suggestion"
                        >
                          📍 {pred.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="search-bar products-mobile-search-cell">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="products-controls-mobile-row products-controls-mobile-row--secondary">
                <div className="sort-dropdown sort-dropdown--mobile-inline">
                  <select
                    value={displayedSortValue}
                    onChange={handleSortChange}
                  >
                    <option value="distance" disabled={!userCoords}>
                      Nearest
                    </option>
                    <option value="popularity">Filter: Popularity</option>
                    <option value="price-low">Filter: Price Low</option>
                    <option value="price-high">Filter: Price High</option>
                    <option value="newest">Filter: Newest</option>
                  </select>
                </div>
                <div className="sort-dropdown sort-dropdown--mobile-inline products-radius-dropdown products-radius-dropdown--mobile">
                  <select
                    value={radiusMiles}
                    onChange={(e) => setRadiusMiles(e.target.value)}
                  >
                    {radiusOptions.map((radius) => (
                      <option key={radius} value={String(radius)}>
                        Radius: {radius} mi
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="products-location-summary products-location-summary--mobile">
                {userCoords ? (
                  <p className="products-location-note products-location-note--active products-location-note--compact">
                    {usingBrowserLocation ? (
                      <>
                        Pickup products near <strong>your location</strong>, sorted by distance ({radiusMiles} mi).
                      </>
                    ) : (
                      <>
                        Pickup products near <strong>{userCoords.label}</strong>, sorted by distance ({radiusMiles} mi).
                      </>
                    )}
                  </p>
                ) : (
                  <p className="products-location-note products-location-note--compact">
                    Location off, using the default discovery feed.
                  </p>
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="products-content">
              {loading ? (
                <div className="products-loading">Loading products...</div>
              ) : error ? (
                <div className="products-error">{error}</div>
              ) : sortedProducts.length === 0 ? (
                <div className="products-empty">
                  <p>
                    {userCoords
                      ? `No products found near this location within ${radiusMiles} miles`
                      : "No products found"}
                    {selectedCategory !== "all" ? " in this category" : ""}.
                  </p>
                </div>
              ) : (
                <div className="products-grid">
                  {sortedProducts.map((product) => (
                    (() => {
                      const sellerName = getSellerDisplayName(product);

                      return (
                    <div
                      key={product._id}
                      className="product-card"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="product-image">
                        <img
                          src={
                            product.images?.[0] ||
                            product.imageUrl ||
                            "/default-product.png"
                          }
                          alt={product.name}
                        />
                      </div>
                      <div className="product-info">
                        <h3>{product.name}</h3>
                        {product.brand && (
                          <p className="product-brand">{product.brand}</p>
                        )}
                        <p className="product-price">
                          ${product.price?.toFixed(2)}
                        </p>
                        {sellerName && (
                          <p className="product-shop">
                            Sold by {sellerName}
                          </p>
                        )}
                        <button
                          type="button"
                          className="product-card-add-to-cart"
                          onClick={async (event) => {
                            event.stopPropagation();

                            if (hasSelectableVariants(product)) {
                              handleProductClick(product);
                              return;
                            }

                            const result = await addToCart({
                              _id: product._id,
                              shopId:
                                typeof product.shopId === "object"
                                  ? product.shopId._id
                                  : product.shopId,
                            });
                            if (!result?.success) {
                              window.alert(
                                result?.error || "Failed to add to cart",
                              );
                            }
                          }}
                        >
                          {hasSelectableVariants(product)
                            ? "View Options"
                            : "Add to Cart"}
                        </button>
                      </div>
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CustomerFooter />
    </div>
  );
}

export default Products;
